import { LatLngTuple } from 'leaflet';

// Color sources
import algaePlantIcon from 'bundle-text:../img/icons/algaeplant.svg';
import bacteriaIcon from 'bundle-text:../img/icons/bacteria.svg';
import flowerWasteIcon from 'bundle-text:../img/icons/flower.svg';
import foodWasteIcon from 'bundle-text:../img/icons/foodwaste.svg';
import plantIcon from 'bundle-text:../img/icons/plant.svg';
import postConsumerWasteIcon from 'bundle-text:../img/icons/clotheswasteplant.svg';
import wasteStreamsIcon from 'bundle-text:../img/icons/waste-streams.svg';
import mineralIcon from 'bundle-text:../img/icons/mineral.svg';
// Methods
import naturalDyeingIcon from 'bundle-text:../img/icons/natural-dyeing.svg';
import bacterialDyeingIcon from 'bundle-text:../img/icons/bacterial-dyeing.svg';

export type TagInfo = {
  name: string;
  icon: string;
};

export type MarkerData = {
  company: string;
  description?: string;
  colourSources: LegendEntry[];
  infoTags?: TagInfo[];
  instagram?: string;
  website?: string;
  tags?: string[];
  location: string;
  latLng: LatLngTuple;
  markerSvg: string;
  communityPage?: string;
};

type LegendEntry = TagInfo & {
  filterBy?: string[];
};

export const colourSources: Record<string, LegendEntry> = {
  plant: {
    icon: plantIcon,
    name: 'plant',
  },
  seaweed: {
    name: 'seaweed',
    icon: algaePlantIcon,
  },
  algae: {
    name: 'algae',
    icon: algaePlantIcon,
  },
  microalgae: {
    name: 'microalgae',
    filterBy: ['algae'],
    icon: algaePlantIcon,
  },
  bacteria: {
    icon: bacteriaIcon,
    name: 'bacteria',
  },
  bacteriaAndFungi: {
    icon: bacteriaIcon,
    filterBy: ['bacteria', 'fungi'],
    name: 'bacteria, fungi',
  },
  foodWaste: {
    icon: foodWasteIcon,
    name: 'food waste',
  },
  flowerWaste: {
    icon: flowerWasteIcon,
    name: 'flower waste',
  },
  postConsumerWaste: {
    icon: postConsumerWasteIcon,
    name: 'postconsumer waste',
  },
  minerals: {
    icon: mineralIcon,
    name: 'minerals',
  },
  rustIron: {
    icon: mineralIcon,
    filterBy: ['minerals', 'rust', 'iron'],
    name: 'rust, iron',
  },
};

export const additionalInfoTags = {
  naturalDyeing: {
    name: 'natural dyeing',
    icon: naturalDyeingIcon,
  },
  bacterialDyeingIcon: {
    name: 'bacterial dyeing',
    icon: bacterialDyeingIcon,
  },
  wasteStreams: {
    name: 'waste streams',
    icon: wasteStreamsIcon,
  },
};

// Coordinates from https://docs.mapbox.com/playground/geocoding/
export const partners: MarkerData[] = [
  {
    company: 'Anima by Loreto',
    description:
      'is a brand experimenting with new ways of applying colour in futuristic, creative and responsible designs. Their garments are made exclusively with local, organic, 100% natural, recycled, or deadstock textiles. The production is carried out on an equitable basis, supporting local communities, and ensuring profits are shared fairly. Loreto herself has been researching and applying bacteria dyes to some of her most special garments. She offers consulting services on innovative and sustainable dyes for the fashion industry and is open to partnerships with researchers and labs, especially for R&D on micro organic dyes.',
    colourSources: [colourSources.plant, colourSources.bacteria],
    infoTags: [
      additionalInfoTags.naturalDyeing,
      additionalInfoTags.wasteStreams,
    ],
    // instagram: 'https://instagram.com/animabyloreto',
    // website: 'www.animabyloreto.com',
    tags: ['Fashion Brand', 'Consultancy'],
    location: 'Barcelona, Spain',
    latLng: [41.382894, 2.177432],
    markerSvg: plantIcon,
  },
  {
    company: 'ZEEFIER',
    description:
      'is a startup that researches and develops natural dyes made out of seaweed. Their vision is to change the textile industry through the innovation of seaweed dyes. They dye yarns, textiles and whole garments and are open to work with both textile producers, as well as brands and designers. You can find more information about Zeefier <a href="./community-zeefier.html" target="_blank">here</a>.',
    colourSources: [colourSources.seaweed],
    infoTags: [
      additionalInfoTags.naturalDyeing,
      additionalInfoTags.wasteStreams,
    ],
    // instagram: 'https://instagram.com/zee.fier',
    // website: 'https://zeefier.eu',
    tags: ['STARTUP'],
    location: 'the Hague, the Netherlands',
    latLng: [52.08417, 4.3175],
    markerSvg: algaePlantIcon,
  },
  {
    company: 'Monica Louise Hartvigsen',
    description:
      "is a biodesigner and a PhD student at Design School Kolding, currently one year into her project “Co-crafting colours”, where she is exploring the design potential of microbial colouring agents. Monica offers colour expertise, product development and is open to collaborate as a research partner, both on smaller scale and industrial projects for dyeing with bacteria. Read more about Monica's work here.",
    colourSources: [colourSources.bacteria],
    // instagram: 'https://www.instagram.com/monicahartvigsen',
    // website: 'https://monicahartvigsen.com',
    tags: ['Researcher', 'Designer'],
    location: 'Kolding, Denmark',
    latLng: [55.49, 9.47],
    markerSvg: bacteriaIcon,
  },
  {
    company: 'Katrina Wilde',
    description:
      "is an artist working with plants, people and materials. Her practice centres on collaboration, community engagement and connections with local ecology; Katrina is open to working with researchers, small sustainable brands, costume designers and individual artists, as well as farmers. Katrina's dyestuff comes from home-grown plants in her allotment, food waste of local restaurants, floral waste and responsible foraging and trusted suppliers. She works with dyeing, printmaking and drawing processes using plant and mineral colour, applying colour to a range of materials; from bio materials to vintage and preloved textiles. She graduated with an MA in Textiles from the Royal College of Art in 2021.",
    colourSources: [
      colourSources.plant,
      colourSources.foodWaste,
      colourSources.flowerWaste,
    ],
    // instagram: 'https://www.instagram.com/wildestudio_',
    // website: 'https://katrinawilde.co.uk',
    tags: ['Textile Artist', 'Dyer', 'Facilitator'],
    location: 'Stoke-on-Trent, England, United Kingdom',
    latLng: [53.01620139999998, -2.1812607],
    markerSvg: plantIcon,
  },
  {
    company: 'Living Colour',
    description:
      'is a biodesign research project developed by Laura Luchtman and Ilfa Siebenhaar, designers who explore the possibilities of natural textile dyeing with bacteria that produce pigment. They use several techniques of bacterial dyeing and work with environmentally-friendly silks (peace silk), materials that come from overstock, and new innovative materials that are not on the market yet. Living Colour currently partners up on B2B projects for research and commissions with fashion brands, dyers, museums and other industries. They are open to collaborate with like-minded people and explore B2C possibilities.', // Read more about Living Colour's approach in our interview here.",
    colourSources: [colourSources.bacteria],
    // instagram: 'https://www.instagram.com/livingcolourcollective',
    // website: 'https://www.livingcolour.eu',
    tags: ['Researcher', 'Designer', 'Collective'],
    location: 'Rotterdam, The Netherlands',
    latLng: [51.95, 4.41667],
    markerSvg: bacteriaIcon,
  },
  {
    company: 'DZIKIE BARWY (wild tones)',
    description:
      'is a natural dyeing studio founded by an author Ola Bystry. It is based in a Polish town known for its rich textile history and craftsmanship - Łódź. Dzikie Barwy offers artistic and educational workshops, product design, dyeing services for fashion brands, plant-based dyes, natural textiles and fibres, as well as the book about natural dyeing. They are open to collaborate with slow fashion brands, artists and designers on botanical colour creation.', // Read more about Dzikie Barwy's approach in our interview here.",
    colourSources: [colourSources.plant],
    // instagram: 'https://www.instagram.com/dzikie_barwy',
    // website: 'https://dzikiebarwy.com',
    tags: ['Dyer', 'Educator'],
    location: 'Łódź, Poland',
    latLng: [51.75949, 19.45851],
    markerSvg: plantIcon,
  },
  // {
  //   company: 'themazi',
  //   description: '',
  //   colourSources: [colourSources.plant],
  // instagram: 'https://www.instagram.com/themazi_dyes',
  // website: 'https://themazi.com/',
  //   // tags: ['STARTUP'],
  //   location: 'Antalya, Turkey',
  //   latLng: [36.9, 30.68333],
  //   markerSvg: plant,
  // },
  {
    company: 'Rebecca Hoyes and Jo Pierce / Colour Matter',
    description:
      'are design researchers at Central Saint Martins, University of the Arts, London, focused on material and colour research, textile print design and education. Their specialty lies in a nuanced approach to colour, its provenance, transience and communication. With a sustainable mindset Colour/Matter explore varied approaches to colour application and offer consultancy, curation and workshops to brands looking to develop sustainable colour strategies. They are open to collaborate on research development and showcasing opportunities, with brands, scientists, industry and research partners.', // Read more about Colour/Matter's approach here.",
    colourSources: [colourSources.foodWaste, colourSources.seaweed],
    // instagram: 'https://www.instagram.com/poetic_colour',
    // website: 'https://www.colour-matter.com/',
    tags: ['Designer', 'Researcher', 'Educator', 'Collective'],
    location: 'London, United Kingdom',
    latLng: [51.507321899999994, -0.96],
    markerSvg: plantIcon,
  },
  {
    company: 'Vienna Textile Lab',
    description:
      'is a startup that uses biology and naturally occurring bacteria to manufacture dyes and pigments for the textile industry. They work with industrial customers, small fashion brands and designers, and offer dyeing-as-a-service, yarns, textiles, dyes, pigments and antimicrobial treatments. VTL currently focuses on funded projects and is actively looking for investors. They are open to partner up with dye houses, textile mills, dye and pigment manufacturers, integrated fashion companies, medical, space, automotive and aviation textile companies - based in Europe, Americas and Asia.', // Read more about Vienna Textile Lab\'s approach in our blog post here.',
    colourSources: [colourSources.bacteria],
    // instagram: 'https://www.instagram.com/poetic_colour',
    // website: 'http://www.viennatextilelab.at',
    tags: ['Startup', 'Dyer', 'Biorefinery'],
    location: 'Vienna, Austria',
    latLng: [48.20833, 16.37306],
    markerSvg: bacteriaIcon,
  },
  {
    company: 'Julia Kromolicka',
    description:
      'designs and creates fabrics, experimental materials, garments, costumes and accessories. Combining forgotten crafts and modern research, she offers natural dyeing, fashion and textile design, patternmaking, tailoring (small quantity), and workshops. In her work she mostly uses deadstock fabrics. Julia is currently working on several projects with fashion brands, designers, artists and creates her own collection of clothing and accessories. She is on the lookout to partner up with fashion brands, designers, artists and showrooms.',
    colourSources: [colourSources.plant],
    // instagram: 'https://www.instagram.com/julia_kromolicka',
    // website: '',
    tags: ['Designer', 'Textile Maker', 'Dyer'],
    location: 'Poznań, Poland',
    latLng: [52.4, 16.91667],
    markerSvg: plantIcon,
  },
  {
    company: 'Institut održive mode (IOM)',
    description:
      'from Croatia is an educator and a small, local fashion business. Coming from a background in textile technology and engineering, IOM’s founder Ivana is an author and a researcher. She produces clothing locally and transparently in Croatia and experiments with applying natural colour from various sources. IOM is open to connect with like-minded people and organisations, to learn and to collaborate.',
    colourSources: [
      colourSources.plant,
      colourSources.foodWaste,
      colourSources.rustIron,
    ], //also rust and fermentation
    infoTags: [additionalInfoTags.naturalDyeing],
    // instagram: 'https://www.instagram.com/institutodrzivemode',
    // website: 'https://institutodrzivemode.hr',
    tags: ['Small Local Business', 'Author', 'Educator'],
    location: 'Koprivnica, Croatia',
    latLng: [46.162359, 16.830809],
    markerSvg: plantIcon, //FIXME should accept multiple
  },
  {
    company: 'Roua Atelier',
    description:
      'Amsterdam was set up by Roua ALHalabi, a natural dyeing developer who combines many handmade and forgotten techniques in her work. Her mission is to promote usage of natural dyes and plants, by bringing to life colour made with plant-based dye recipes and techniques. Roua designs and offers workshops for both kids and adults, as well as develops colours by innovating traditional ways of dyeing.',
    colourSources: [
      colourSources.plant,
      colourSources.foodWaste,
      colourSources.minerals,
    ],
    infoTags: [additionalInfoTags.naturalDyeing],
    // instagram: 'https://www.instagram.com/rouaatelieramsterdam',
    // website: 'http://rouaatelier.com',
    tags: ['Dyer', 'Colour developer', 'Educator'],
    location: 'Amsterdam, The Netherlands',
    latLng: [52.37276, 4.893604],
    markerSvg: plantIcon,
  },
  {
    company: 'Ruth Lloyd',
    description:
      'is a London based, textile-led design researcher. She develops practice-based research focused on the creative and industrial potential of microbial pigments, investigating how these living colour systems present solutions and replacements to the global reliance on toxic petroleum-based colour and dye systems. Her current work focuses on the application of colourants produced by microorganisms such as bacteria for textile screen-printing processes. Ruth engages with design education, research & development, and industry partners. She is currently undertaking a PhD at the Living Systems Research Lab at Central Saint Martins; and is the first Creative Resident at Colorifix Ltd, a Norwich based biotech startup commercialising bacterial dye processes. Microbial dyeing, screen-printing.',
    colourSources: [colourSources.bacteriaAndFungi, colourSources.algae],
    // infoTags: [], //microbial dyeing
    // instagram: 'https://www.instagram.com/ruth_lloyd_design',
    // website: 'https://ruthlloyddesign.co.uk',
    tags: ['Researcher', 'Educator'],
    location: 'London, United Kingdom',
    latLng: [51.507322, -0.027647],
    markerSvg: algaePlantIcon,
  },
  {
    company: 'Why Colours Matter',
    description:
      'is an interdisciplinary project by sustainable fashion designer (M.A) Anne-Marie Sust, winner of Green Concept Award 2022 in the fashion category, who combines design and natural science, resulting in innovative microalgae inks for printing on fashion textiles. She is currently expanding the collection’s palette, testing colours on new surfaces and bringing the microalgae inks closer to consumers. Why Colours Matter is looking to cooperate with sustainable fashion labels on design, as well as with artists and companies beyond fashion, who are interested in trying out her microalgae colours on new surfaces. Screen printing with microalgae based inks.',
    colourSources: [colourSources.microalgae],
    // instagram: 'https://www.instagram.com/why.colours.matter',
    // website: 'https://www.annemariesust.com',
    tags: ['Designer', 'Lab'],
    location: 'Cologne, Germany',
    latLng: [51.507322, 6.959974],
    markerSvg: algaePlantIcon,
  },
  // {
  //   company: '',
  //   description:
  //     '',
  //   infoTags: [],
  //   colourSources: [],
  //   instagram: 'https://www.instagram.com/',
  //   website: '',
  //   tags: [],
  //   location: '',
  //   latLng: [1, 0],
  //   markerSvg: '',
  // },
];

export const legendEntries = [
  colourSources.plant,
  colourSources.seaweed,
  colourSources.algae,
  colourSources.bacteria,
  colourSources.foodWaste,
  colourSources.flowerWaste,
  // colourSources.postConsumerWaste, // Nobody on the map uses it, so hide it
  colourSources.minerals,
];
